import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useRouteCourse } from "../../hooks/useRouteCourse";
import { learnPath } from "../../utils/routes";
import { AnimatePresence, motion, PanInfo, useReducedMotion } from "framer-motion";
import { ArrowLeftIcon, CheckIcon, ChevronLeftIcon, ChevronRightIcon, LockIcon, XIcon } from "lucide-react";
import { toast } from "sonner";
import { useLearner } from "../../contexts/LearnerContext";
import { useWords } from "../../contexts/WordsContext";
import { bookChapters, marketChapterPages } from "../../data/book";
import { lessonSpeechLang } from "../../data/lessonContent";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { BookPageView } from "../../components/reader/BookPageView";
import { WordSheet } from "../../components/reader/WordSheet";
import { IconButton } from "../../components/ui/IconButton";
import { ThemeToggle } from "../../components/ui/ThemeToggle";
import { ProgressBar } from "../../components/ui/ProgressBar";
import { BrutalButton } from "../../components/ui/BrutalButton";
import { spring } from "../../utils/motion";
export function Reader() {
  const {
    setBookPage
  } = useLearner();
  const {
    saveWord,
    isSaved
  } = useWords();
  const course = useRouteCourse();
  const desktop = useMediaQuery("(min-width: 1024px)");
  const reduce = useReducedMotion() ?? false;
  const pages = marketChapterPages;
  const [page, setPage] = useState(Math.min(course?.bookPage ?? 0, pages.length - 1));
  const [dir, setDir] = useState(1);
  const [word, setWord] = useState<{
    term: string;
    translation: string;
  } | null>(null);
  const refs = useRef<(HTMLDivElement | null)[]>([]);
  const chapter = bookChapters.find(c => c.status === "reading")!;
  useEffect(() => {
    if (course) setBookPage(course.id, page);
  }, [course, page, setBookPage]);
  useEffect(() => {
    if (!desktop) return;
    const obs = new IntersectionObserver(entries => {
      const visible = entries.filter(e => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (visible) setPage(Number((visible.target as HTMLElement).dataset.index));
    }, {
      rootMargin: "-30% 0px -50% 0px",
      threshold: [0, 0.25, 0.5]
    });
    refs.current.forEach(el => el && obs.observe(el));
    return () => obs.disconnect();
  }, [desktop]);
  useEffect(() => {
    if (desktop && course?.bookPage) refs.current[course.bookPage]?.scrollIntoView({
      block: "start"
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desktop]);
  if (!course) return <Navigate to="/app" replace data-id="element-1469" data-mp="34qa47h2fijd4" />;
  const courseId = course.id;
  const backTo = learnPath(course);
  const go = (next: number) => {
    if (next < 0 || next >= pages.length) return;
    setDir(next > page ? 1 : -1);
    setWord(null);
    if (desktop) refs.current[next]?.scrollIntoView({
      behavior: reduce ? "auto" : "smooth",
      block: "start"
    });else setPage(next);
  };
  const onDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.x < -80 || info.velocity.x < -400) go(page + 1);else if (info.offset.x > 80 || info.velocity.x > 400) go(page - 1);
  };
  const save = (term: string, translation: string) => {
    const {
      created
    } = saveWord({
      term,
      translation,
      courseId,
      source: "book"
    });
    toast.success(created ? `“${term}” saved to My Words` : "Already in My Words", {
      description: created ? "It'll appear in your next lessons." : undefined
    });
  };
  const onWord = (term: string, translation: string) => setWord({
    term,
    translation
  });
  const pageProps = (i: number) => ({
    page: pages[i],
    pageNumber: i + 1,
    speechLang: lessonSpeechLang,
    activeWord: word?.term.toLowerCase(),
    onWord,
    isSaved: (t: string) => isSaved(t, courseId),
    onSaveVocab: save
  });
  return <div className="min-h-screen w-full bg-paper font-sans text-ink" data-id="element-1470" data-mp="2satwoh7rqdgo">
      <header className="sticky top-0 z-40 border-b-3 border-line bg-paper/90 backdrop-blur-md" data-id="element-1471" data-mp="2n8uu39jurkks">
        <div className="mx-auto flex h-16 max-w-6xl items-center gap-4 px-4 md:px-8" data-id="element-1472" data-mp="wtu6khmxezjf">
          <IconButton label="Close book" to={backTo} data-id="element-1473" data-mp="11sn9xh8i1a5v">
            <XIcon className="h-5 w-5" data-id="element-1474" data-mp="15t77un0wcht2" />
          </IconButton>
          <div className="min-w-0 flex-1" data-id="element-1475" data-mp="15rnscyn2easc">
            <p className="truncate text-sm font-bold text-ink" lang="es" data-id="element-1476" data-mp="1asaay1xbgdye">
              Chapter {chapter.number} · {chapter.title}
            </p>
            <div className="mt-1 flex items-center gap-3" data-id="element-1477" data-mp="1fqloonqqf8lw">
              <ProgressBar value={(page + 1) / pages.length * 100} size="sm" className="max-w-[220px] flex-1" label="Chapter progress" data-id="element-1478" data-mp="1kq7yen7cq6py" />
              <span className="shrink-0 text-xs font-semibold text-ink-2" data-id="element-1479" data-mp="1pihqa8s11guv">
                Page {page + 1} of {pages.length}
              </span>
            </div>
          </div>
          <ThemeToggle data-id="element-1480" data-mp="1yinypg8vbqn6" />
        </div>
      </header>

      <div className="mx-auto grid max-w-6xl gap-8 px-4 py-6 md:px-8 lg:grid-cols-[240px_1fr] lg:py-10" data-id="element-1481" data-mp="1yg1plc6bx32u">
        <aside className="hidden lg:block" data-id="element-1482" data-mp="23emuhowj0axm">
          <nav aria-label="Chapters" className="sticky top-24 space-y-6" data-id="element-1483" data-mp="2dlxoxt6c8imx">
            <div data-id="element-1484" data-mp="28la1fhhbijxg">
              <h2 className="text-xs font-bold text-ink-2" data-id="element-1485" data-mp="28krw7vcogvde">This chapter</h2>
              <ol className="mt-2 space-y-1" data-id="element-1486" data-mp="23mgkff2toasj">
                {pages.map((p, i) => <li key={p.id} data-id="element-1487" data-mp="1zl4vlbrrvtle" data-mpi={i}>
                    <button type="button" onClick={() => go(i)} aria-current={i === page ? "true" : undefined} className={`focus-ring flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-sm font-semibold transition-colors duration-150 ${i === page ? "border-2 border-line bg-accent text-night" : "text-ink-2 hover:bg-sunken hover:text-ink"}`} data-id="element-1488" data-mp="1zm6icdi5i4iy">
                      <span className="w-5 text-xs" data-id="element-1489" data-mp="3f3gcqap66pmk">{i + 1}</span>
                      <span className="truncate" lang="es" data-id="element-1490" data-mp="3aanbhwswtzf8">{p.title}</span>
                    </button>
                  </li>)}
              </ol>
            </div>
            <div data-id="element-1491" data-mp="1dcpwcl6bjh14">
              <h2 className="text-xs font-bold text-ink-2" data-id="element-1492" data-mp="1dd94ja662r1y">Book</h2>
              <ol className="mt-2 space-y-1 text-sm" data-id="element-1493" data-mp="1dek8b9zm3yt2">
                {bookChapters.map(c => <li key={c.id} className="flex items-center gap-2 px-3 py-1.5 text-ink-2" data-id="element-1494" data-mp="ylz4jd4q8hy0">
                    {c.status === "read" ? <CheckIcon className="h-4 w-4 text-good" strokeWidth={3} data-id="element-1495" data-mp="pmvjw4pwvuhe" /> : c.status === "locked" ? <LockIcon className="h-3.5 w-3.5" data-id="element-1496" data-mp="unjcuvxpale9" /> : <span className="h-2.5 w-2.5 rounded-full border-2 border-line bg-accent" data-id="element-1497" data-mp="fq91dml25fij" />}
                    <span className={c.status === "reading" ? "font-bold text-ink" : ""} lang="es" data-id="element-1498" data-mp="koal89qb5iv2">
                      {c.number}. {c.title}
                    </span>
                  </li>)}
              </ol>
            </div>
            <Link to={backTo} className="focus-ring flex items-center gap-1.5 rounded-lg px-3 text-sm font-semibold text-ink-2 hover:text-ink" data-id="element-1499" data-mp="3e6o17owqcig">
              <ArrowLeftIcon className="h-4 w-4" data-id="element-1500" data-mp="3umluxrbdmmcp" /> Back to course
            </Link>
          </nav>
        </aside>

        <main className="min-w-0" data-id="element-1501" data-mp="d6m8cbnofd6i">
          <p className="mb-4 text-sm text-ink-2" data-id="element-1502" data-mp="8day00jnoe9u">
            Tap any <span className="underline decoration-brand/60 decoration-dotted decoration-2 underline-offset-4" data-id="element-1503" data-mp="8bq1i5ds6l9c">underlined word</span> to translate and save it.
          </p>
          {desktop ? <div className="space-y-10" data-id="element-1504" data-mp="i3cikicsvek0">
              {pages.map((_, i) => <div key={pages[i].id} ref={el => refs.current[i] = el} data-index={i} className="scroll-mt-24" data-id="element-1505" data-mp="w2vgyjft7bj4" data-mpi={i}>
                  <BookPageView {...pageProps(i)} data-id="element-1506" data-mp="r27sa4f1z6s2" />
                </div>)}
              <div className="flex justify-center pt-4" data-id="element-1507" data-mp="2xv0hgjbdjh5g">
                <BrutalButton to={backTo} size="lg" data-id="element-1508" data-mp="2sw75wwfne07x">
                  Finish chapter
                </BrutalButton>
              </div>
            </div> : <>
              <div className="overflow-hidden pb-2 pr-2" data-id="element-1509" data-mp="2c1bbmi3yuin8">
                <AnimatePresence mode="popLayout" initial={false} custom={dir} data-id="element-1510" data-mp="2bzrw4tq4wbmi">
                  <motion.div key={page} custom={dir} initial={reduce ? {
                opacity: 0
              } : {
                opacity: 0,
                x: dir * 60
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={reduce ? {
                opacity: 0
              } : {
                opacity: 0,
                x: dir * -60
              }} transition={spring.smooth} drag={reduce ? false : "x"} dragConstraints={{
                left: 0,
                right: 0
              }} dragElastic={0.25} onDragEnd={onDragEnd} className="touch-pan-y" data-id="element-1511" data-mp="2gtmdhqi9oeif">
                    <BookPageView {...pageProps(page)} data-id="element-1512" data-mp="39gp5s0wcnt08" />
                  </motion.div>
                </AnimatePresence>
              </div>
              <div className="mt-6 flex items-center justify-between gap-3" data-id="element-1513" data-mp="12sggum3e877b">
                <BrutalButton variant="surface" onClick={() => go(page - 1)} disabled={page === 0} ariaLabel="Previous page" data-id="element-1514" data-mp="12uj44yd5vm8c">
                  <ChevronLeftIcon className="h-5 w-5" data-id="element-1515" data-mp="t2n25c7yfbyu" /> Prev
                </BrutalButton>
                <ol className="flex gap-1.5" aria-hidden="true" data-id="element-1516" data-mp="o43hdf53k682">
                  {pages.map((p, i) => <li key={p.id} className={`h-2.5 rounded-full border-2 border-line ${i === page ? "w-6 bg-accent" : "w-2.5 bg-sunken"}`} data-id="element-1517" data-mp="j58ofkdf11a8" data-mpi={i} />)}
                </ol>
                {page === pages.length - 1 ? <BrutalButton to={backTo} data-id="element-1518" data-mp="a4u34l0gbm6q">Finish</BrutalButton> : <BrutalButton onClick={() => go(page + 1)} ariaLabel="Next page" data-id="element-1519" data-mp="55zlvni4u5oq">
                    Next <ChevronRightIcon className="h-5 w-5" data-id="element-1520" data-mp="24ibs8cgi8r3v" />
                  </BrutalButton>}
              </div>
            </>}
        </main>
      </div>

      <WordSheet word={word} saved={word ? isSaved(word.term, courseId) : false} speechLang={lessonSpeechLang} onClose={() => setWord(null)} onSave={() => word && save(word.term.toLowerCase(), word.translation)} data-id="element-1521" data-mp="7xhsgfpulymr" />
    </div>;
}